/**
 * Theme Mode Context
 * 
 * Manages light/dark mode switching for static parts of the site
 * (not the dynamic Leonardo demo in Hero)
 */

/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type ThemeMode = 'light' | 'dark';

interface ThemeModeContextType {
  mode: ThemeMode;
  toggleMode: () => void;
  setMode: (mode: ThemeMode) => void;
  resetToSystemPreference: () => void;
  isFollowingSystem: boolean;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

interface ThemeModeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
}

export const ThemeModeProvider: React.FC<ThemeModeProviderProps> = ({
  children,
}) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    // Read the class set by the blocking script in index.html
    // This ensures React state is perfectly in sync with the DOM
    if (document.documentElement.classList.contains('dark-mode')) {
      return 'dark';
    }
    return 'light';
  });

  // Track if user has explicitly set a preference (vs. following OS)
  const [hasUserPreference, setHasUserPreference] = useState<boolean>(() => {
    return localStorage.getItem('theme-mode') !== null;
  });

  // Listen for OS preference changes (only if user hasn't set explicit preference)
  useEffect(() => {
    if (hasUserPreference) return; // User has explicit preference, don't follow OS

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setMode(e.matches ? 'dark' : 'light');
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [hasUserPreference]);

  useEffect(() => {
    // Apply theme class to document root
    const root = document.documentElement;
    
    if (mode === 'dark') {
      root.classList.add('dark-mode');
      root.classList.remove('light-mode');
    } else {
      root.classList.add('light-mode');
      root.classList.remove('dark-mode');
    }
    
    // Only save to localStorage if user has explicitly set preference
    if (hasUserPreference) {
      localStorage.setItem('theme-mode', mode);
    }
  }, [mode, hasUserPreference]);

  const toggleMode = () => {
    setHasUserPreference(true); // User is now explicitly choosing
    setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
  };

  // Allow explicit mode setting (marks as user preference)
  const setModeExplicit = (newMode: ThemeMode) => {
    setHasUserPreference(true);
    setMode(newMode);
  };

  // Reset to follow OS preference
  const resetToSystemPreference = () => {
    localStorage.removeItem('theme-mode');
    setHasUserPreference(false);
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setMode(prefersDark ? 'dark' : 'light');
  };

  return (
    <ThemeModeContext.Provider value={{ 
      mode, 
      toggleMode, 
      setMode: setModeExplicit,
      resetToSystemPreference,
      isFollowingSystem: !hasUserPreference 
    }}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = (): ThemeModeContextType => {
  const context = useContext(ThemeModeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};
